// Client-side country detection utility with server-side IP detection
export async function detectUserCountryFromIP(): Promise<string> {
  try {
    // Call our internal API route with aggressive timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1500); // 1.5s timeout
    
    const response = await fetch('/api/detect-country', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      cache: 'force-cache' // Cache responses
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success && data.country_code) {
      return data.country_code;
    } else {
      throw new Error('No country code returned');
    }
  } catch (error) {
    // Silent fail to avoid console spam from rate limiting
    return detectUserCountryFromBrowser();
  }
}
export function getCountryFromTimezone(): string {
  try {
    // Get user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Basic timezone to country mapping for common cases
    const timezoneToCountry: { [key: string]: string } = {
      // Middle East
      'Asia/Dubai': 'AE',
      'Asia/Kuwait': 'KW',
      'Asia/Qatar': 'QA',
      'Asia/Bahrain': 'BH',
      'Asia/Riyadh': 'SA',
      'Asia/Muscat': 'OM',
      
      // Europe
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Zurich': 'CH',
      'Europe/Vienna': 'AT',
      'Europe/Stockholm': 'SE',
      'Europe/Oslo': 'NO',
      'Europe/Copenhagen': 'DK',
      'Europe/Helsinki': 'FI',
      'Europe/Dublin': 'IE',
      'Europe/Lisbon': 'PT',
      'Europe/Warsaw': 'PL',
      'Europe/Prague': 'CZ',
      'Europe/Budapest': 'HU',
      'Europe/Athens': 'GR',
      'Europe/Bucharest': 'RO',
      'Europe/Sofia': 'BG',
      'Europe/Zagreb': 'HR',
      'Europe/Ljubljana': 'SI',
      'Europe/Bratislava': 'SK',
      'Europe/Tallinn': 'EE',
      'Europe/Riga': 'LV',
      'Europe/Vilnius': 'LT',
      'Europe/Kiev': 'UA',
      'Europe/Moscow': 'RU',
      
      // North America
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Toronto': 'CA',
      'America/Vancouver': 'CA',
      'America/Montreal': 'CA',
      'America/Mexico_City': 'MX',
      
      // Asia Pacific
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Asia/Singapore': 'SG',
      'Asia/Bangkok': 'TH',
      'Asia/Jakarta': 'ID',
      'Asia/Manila': 'PH',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Karachi': 'PK',
      'Asia/Kolkata': 'IN',
      'Asia/Colombo': 'LK',
      'Asia/Dhaka': 'BD',
      'Asia/Taipei': 'TW',
      'Asia/Ho_Chi_Minh': 'VN',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU',
      'Pacific/Auckland': 'NZ',
      
      // Africa
      'Africa/Cairo': 'EG',
      'Africa/Lagos': 'NG',
      'Africa/Johannesburg': 'ZA',
      'Africa/Nairobi': 'KE',
      'Africa/Casablanca': 'MA',
      'Africa/Algiers': 'DZ',
      'Africa/Tunis': 'TN',
      
      // South America
      'America/Sao_Paulo': 'BR',
      'America/Argentina/Buenos_Aires': 'AR',
      'America/Lima': 'PE',
      'America/Bogota': 'CO',
      'America/Santiago': 'CL',
      'America/Caracas': 'VE',
    };
    
    return timezoneToCountry[timezone] || 'AE'; // Default to UAE
  } catch (error) {
    console.log('Could not detect country from timezone, using default');
    return 'AE'; // Default to UAE
  }
}

export function getCountryFromLanguage(): string {
  try {
    // Get user's preferred languages
    const languages = navigator.languages || [navigator.language];
    const primaryLanguage = languages[0];
    
    // Basic language to country mapping
    const languageToCountry: { [key: string]: string } = {
      'ar-AE': 'AE',
      'ar-SA': 'SA',
      'ar-KW': 'KW',
      'ar-QA': 'QA',
      'ar-BH': 'BH',
      'ar-OM': 'OM',
      'ar-EG': 'EG',
      'ar-LB': 'LB',
      'ar-JO': 'JO',
      'ar-SY': 'SY',
      'ar-IQ': 'IQ',
      'en-US': 'US',
      'en-GB': 'GB',
      'en-CA': 'CA',
      'en-AU': 'AU',
      'en-NZ': 'NZ',
      'en-IE': 'IE',
      'en-ZA': 'ZA',
      'fr-FR': 'FR',
      'fr-CA': 'CA',
      'fr-BE': 'BE',
      'fr-CH': 'CH',
      'de-DE': 'DE',
      'de-AT': 'AT',
      'de-CH': 'CH',
      'es-ES': 'ES',
      'es-MX': 'MX',
      'es-AR': 'AR',
      'es-CO': 'CO',
      'it-IT': 'IT',
      'it-CH': 'CH',
      'pt-BR': 'BR',
      'pt-PT': 'PT',
      'nl-NL': 'NL',
      'nl-BE': 'BE',
      'ru-RU': 'RU',
      'ja-JP': 'JP',
      'ko-KR': 'KR',
      'zh-CN': 'CN',
      'zh-TW': 'TW',
      'zh-HK': 'HK',
      'hi-IN': 'IN',
      'th-TH': 'TH',
      'vi-VN': 'VN',
      'id-ID': 'ID',
      'ms-MY': 'MY',
      'tl-PH': 'PH',
      'sv-SE': 'SE',
      'no-NO': 'NO',
      'da-DK': 'DK',
      'fi-FI': 'FI',
      'pl-PL': 'PL',
      'cs-CZ': 'CZ',
      'hu-HU': 'HU',
      'ro-RO': 'RO',
      'bg-BG': 'BG',
      'hr-HR': 'HR',
      'sl-SI': 'SI',
      'sk-SK': 'SK',
      'et-EE': 'EE',
      'lv-LV': 'LV',
      'lt-LT': 'LT',
      'el-GR': 'GR',
      'tr-TR': 'TR',
      'he-IL': 'IL',
      'fa-IR': 'IR',
      'ur-PK': 'PK',
      'bn-BD': 'BD',
      'ta-LK': 'LK',
    };
    
    // Check exact match first
    if (languageToCountry[primaryLanguage]) {
      return languageToCountry[primaryLanguage];
    }
    
    // Check language code without region
    const languageCode = primaryLanguage.split('-')[0];
    const fallbackMapping: { [key: string]: string } = {
      'ar': 'AE',
      'en': 'US',
      'fr': 'FR',
      'de': 'DE',
      'es': 'ES',
      'it': 'IT',
      'pt': 'BR',
      'nl': 'NL',
      'ru': 'RU',
      'ja': 'JP',
      'ko': 'KR',
      'zh': 'CN',
      'hi': 'IN',
      'th': 'TH',
      'vi': 'VN',
      'id': 'ID',
      'ms': 'MY',
      'tl': 'PH',
      'sv': 'SE',
      'no': 'NO',
      'da': 'DK',
      'fi': 'FI',
      'pl': 'PL',
      'cs': 'CZ',
      'hu': 'HU',
      'ro': 'RO',
      'bg': 'BG',
      'hr': 'HR',
      'sl': 'SI',
      'sk': 'SK',
      'et': 'EE',
      'lv': 'LV',
      'lt': 'LT',
      'el': 'GR',
      'tr': 'TR',
      'he': 'IL',
      'fa': 'IR',
      'ur': 'PK',
      'bn': 'BD',
      'ta': 'LK',
    };
    
    return fallbackMapping[languageCode] || 'AE';
  } catch (error) {
    console.log('Could not detect country from language, using default');
    return 'AE';
  }
}

// Main function to detect country using multiple methods
export function detectUserCountryFromBrowser(): string {
  try {
    // Try timezone first (more reliable for location)
    const fromTimezone = getCountryFromTimezone();
    if (fromTimezone !== 'AE') {
      return fromTimezone;
    }
    
    // Fallback to language detection
    const fromLanguage = getCountryFromLanguage();
    return fromLanguage;
  } catch (error) {
    console.log('Country detection failed, using default');
    return 'AE'; // Default to UAE
  }
}

// Main function that tries IP detection first, then browser detection
export async function detectUserCountry(): Promise<string> {
  try {
    // Try IP-based detection first (most accurate) with timeout
    const fromIP = await detectUserCountryFromIP();
    return fromIP;
  } catch (error) {
    // Fallback to browser-based detection
    return detectUserCountryFromBrowser();
  }
}